import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  A2PRegistrationInfoInterface,
  A2PRegistrationStatusInterface,
  AuthorizedRepresentativeInterface,
  BrandStatus,
  BusinessInterface,
  DataField,
  RegistrationIdentifierInterface,
  UpsertA2PRegistrationInfoRequestInterface,
  ValidationStatus,
} from '@vendasta/smsv2';
import { Subscription, combineLatest, take } from 'rxjs';
import { SmsRegistrationService } from '../service/sms.service';
import { RegistrationStage } from '../sms.constants';
import { ConfirmationModalComponent } from './comfirmation-dialog/confirmation-modal.component';
import { BusinessCategory, BusinessType, Position, Region } from './types';

// The format for american EINsn(Employer Identification Number) is XX-XXXXXXX
const EIN_REGEX = /^\d{2}-\d{7}$/;

// The format of the phone number is +1 (XXX) XXX-XXXX
const PHONE_NUMBER_REGEX = /^\+\d{1,3}-\d{3}-\d{3}-\d{4}$/;

// The format of zip codes is either XXXXX or XXXXX-XXXX
const ZIP_CODE_REGEX = /^\d{5}(?:-\d{4})?$/;

// IdentifierTypeEIN USA: Employer Identification Number.
const IdentifierTypeEIN = 'EIN';

const additionalAddressLine = 'additionalAddressLine';
const businessRegistrationNumber = 'businessRegistrationNumber';
const businessName = 'businessName';

@Component({
  selector: 'sms-registration-form',
  templateUrl: './sms-registration-form.component.html',
  styleUrls: ['./sms-registration-form.component.scss'],
})
export class SmsRegistrationFormComponent implements OnInit, OnDestroy {
  BusinessCategories = Object.values(BusinessCategory);
  BusinessTypes = Object.values(BusinessType);
  Regions = Object.values(Region);
  JobPositions = Object.values(Position);

  smsForm: FormGroup;
  formLocked = false;
  registrationStage: RegistrationStage = RegistrationStage.NotSubmitted;
  initialRegistrationFormInfo: Map<string, string> = new Map<string, string>();
  fieldsWithIssues: string[] = [];
  groupOfSomeValuesNeedToChange: string[] = [];
  customValidatorsMap = new Map<string, ValidatorFn>();

  subscriptions: Subscription[] = [];

  fieldWithIssuesToFormFieldsMapping = new Map<DataField, string>([
    [DataField.DATA_FIELD_LEGAL_BUSINESS_NAME, 'businessName'],
    [DataField.DATA_FIELD_BUSINESS_CATEGORY, 'businessCategory'],
    [DataField.DATA_FIELD_BUSINESS_TYPE, 'businessType'],
    [DataField.DATA_FIELD_REGION_OF_OPERATION, 'regionOfOperation'],
    [DataField.DATA_FIELD_BUSINESS_REGISTRATION_NUMBER, 'businessRegistrationNumber'],
    [DataField.DATA_FIELD_STREET_ADDRESS, 'address'],
    [DataField.DATA_FIELD_STREET_ADDRESS_SECONDARY, 'additionalAddressLine'],
    [DataField.DATA_FIELD_CITY, 'city'],
    [DataField.DATA_FIELD_STATE, 'state'],
    [DataField.DATA_FIELD_POSTAL_CODE, 'zipCode'],
    [DataField.DATA_FIELD_WEBSITE_URL, 'website'],
    [DataField.DATA_FIELD_COUNTRY, 'country'],
    [DataField.DATA_FIELD_FIRST_NAME, 'firstName'],
    [DataField.DATA_FIELD_LAST_NAME, 'lastName'],
    [DataField.DATA_FIELD_JOB_POSITION, 'jobPosition'],
    [DataField.DATA_FIELD_BUSINESS_TITLE, 'businessTitle'],
    [DataField.DATA_FIELD_EMAIL, 'email'],
    [DataField.DATA_FIELD_PHONE_NUMBER, 'phoneNumber'],
  ]);

  constructor(
    private formBuilder: FormBuilder,
    private smsRegistrationService: SmsRegistrationService,
    private snackbarService: SnackbarService,
    private modal: MatDialog,
    private destroyRef: DestroyRef,
  ) {
    this.smsForm = this.formBuilder.group({
      businessName: ['', Validators.maxLength(64)],
      businessCategory: [''],
      businessType: [''],
      regionOfOperation: [''],
      businessRegistrationNumber: ['', Validators.pattern(EIN_REGEX)],
      address: [''],
      additionalAddressLine: [''],
      state: [''],
      city: [''],
      zipCode: ['', Validators.pattern(ZIP_CODE_REGEX)],
      website: [''],
      firstName: [''],
      lastName: [''],
      jobPosition: [''],
      businessTitle: [''],
      email: ['', Validators.email],
      phoneNumber: ['', Validators.pattern(PHONE_NUMBER_REGEX)],
    });
  }

  ngOnInit(): void {
    this.getRegistration();
    const sub = this.smsForm.controls['jobPosition'].valueChanges.subscribe((value: string) => {
      const businessTitle = this.smsForm.value.businessTitle;
      const businessTitleInJobPositions = this.JobPositions.some((p) => p === businessTitle);

      if (value !== Position.otherPosition && (businessTitle === '' || businessTitleInJobPositions)) {
        this.smsForm.controls['businessTitle'].patchValue(value);
      } else if (value === Position.otherPosition && businessTitleInJobPositions) {
        this.smsForm.controls['businessTitle'].patchValue('');
      }
    });
    this.subscriptions.push(sub);

    Object.keys(this.smsForm.controls).forEach((key) => {
      this.subscriptions.push(
        this.smsForm.controls[key].valueChanges.subscribe(() => {
          if (this.groupOfSomeValuesNeedToChange.includes(key)) {
            this.groupOfSomeValuesNeedToChange.forEach((fieldName) => {
              this.smsForm.controls[fieldName].updateValueAndValidity({ emitEvent: false });
            });
          }
        }),
      );
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  submitRegistrationForm(): void {
    this.toggleSubmitValidators(true);
    if (this.smsForm.invalid) {
      this.snackbarService.openErrorSnack('Invalid data for submission');
      return;
    }

    this.modal
      .open(ConfirmationModalComponent, { width: '420px' })
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.upsertData(true);
        }
      });
  }

  saveChanges(): void {
    // Don't show errors for empty required fields & unchanged error fields, when doing partial save
    this.toggleSubmitValidators(false);

    // Check for formatting errors
    const hasFormatErrors = Object.keys(this.smsForm.controls).some((key) => {
      const errors = this.smsForm.controls[key].errors;
      if (errors === null) {
        return;
      }

      return Object.keys(errors).some((errorKey) => errorKey !== 'required');
    });

    // If there are formatting errors don't save the form
    if (hasFormatErrors) {
      this.snackbarService.openErrorSnack('Invalid format for some fields');

      return;
    }

    this.upsertData(false);
  }

  upsertData(isSubmitted: boolean): void {
    const form = this.smsForm.value;
    //  TODO: get country from form
    const country = 'US';

    const request = {
      business: {
        legalName: form.businessName,
        businessType: form.businessType,
        industry: form.businessCategory,
        registrationIdentifier: {
          identifierType: IdentifierTypeEIN,
          identifier: form.businessRegistrationNumber,
        } as RegistrationIdentifierInterface,
        regionOfOperation: form.regionOfOperation,
        websiteUrl: form.website,
        socialMediaProfileUrls: [],
        streetAddress: form.address,
        streetAddressSecondary: form.additionalAddressLine,
        city: form.city,
        state: form.state,
        postalCode: form.zipCode,
        country: country,
      } as BusinessInterface,
      authorizedRepresentative: {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        businessTitle: form.businessTitle,
        jobPosition: form.jobPosition,
      } as AuthorizedRepresentativeInterface,
      submitted: isSubmitted,
    } as UpsertA2PRegistrationInfoRequestInterface;

    this.smsRegistrationService.upsertA2pRegistration(request).subscribe(
      () => {
        this.snackbarService.openWithOptions('Registration Saved Successfully', {
          panelClass: ['success-snackbar'],
        });
        if (isSubmitted) {
          this.lockForm();
        }
      },
      () => {
        this.snackbarService.openErrorSnack('Error Saving Registration');
      },
    );
  }

  getRegistration(): void {
    combineLatest([
      this.smsRegistrationService.a2PRegistrationInfo$,
      this.smsRegistrationService.registrationStage$,
      this.smsRegistrationService.a2pRegistrationStatus$,
      this.smsRegistrationService.getPrefillA2PFormInfo(),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([a2pRegistrationInfo, registrationStage, registrationStatus, a2PFormInfo]) => {
          this.registrationStage = registrationStage;
          this.prefillFormWithA2PFormInfo(a2PFormInfo as A2PRegistrationInfoInterface);

          if (
            registrationStage !== RegistrationStage.NotSubmitted &&
            registrationStage !== RegistrationStage.DataError
          ) {
            this.lockForm();
          } else {
            this.unlockForm();
          }

          if (registrationStage === RegistrationStage.DataError) {
            this.initialRegistrationFormInfo = this.getInitialRegistrationFormInfo(a2pRegistrationInfo);
            this.fieldsWithIssues = this.getFormFieldsWithIssues(registrationStatus);
            this.groupOfSomeValuesNeedToChange = this.getGroupOfSomeValuesNeedToChange(registrationStatus);
            this.fieldsWithIssues.forEach((fieldName) => {
              const control = this.smsForm.get(fieldName);
              if (control) {
                control.markAsTouched();
              }
            });
            this.groupOfSomeValuesNeedToChange.forEach((fieldName) => {
              const control = this.smsForm.get(fieldName);
              if (control) {
                control.markAsTouched();
              }
            });
            this.toggleSubmitValidators(true);
          }
        },
        (error) => {
          if (error.status !== 404) {
            this.snackbarService.openErrorSnack('Error Fetching Registration');
          }
        },
      );
  }

  lockForm(): void {
    this.formLocked = true;
    this.smsForm.disable();
  }

  unlockForm(): void {
    this.formLocked = false;
    this.smsForm.enable();
  }

  toggleSubmitValidators(on: boolean): void {
    Object.keys(this.smsForm.controls).forEach((key) => {
      const control = this.smsForm.controls[key];
      if (on) {
        if (key !== additionalAddressLine) {
          control.addValidators(Validators.required);
        }
        if (this.fieldsWithIssues.includes(key) && this.registrationStage === RegistrationStage.DataError) {
          const val = this.initialRegistrationFormInfo.get(key);
          if (val !== undefined) {
            const sameValueValidatorFn = this.sameValueValidator(val);
            control.addValidators(sameValueValidatorFn);
            this.customValidatorsMap.set(key, sameValueValidatorFn);
          }
        } else if (
          this.groupOfSomeValuesNeedToChange.includes(key) &&
          this.registrationStage === RegistrationStage.DataError
        ) {
          const someValueNeedsToChange = this.someValueNeedsToChangeValidator(this.groupOfSomeValuesNeedToChange);

          control.addValidators(someValueNeedsToChange);
          this.customValidatorsMap.set(key, someValueNeedsToChange);
        }
      } else {
        control.removeValidators(Validators.required);
        const customValidatorFn = this.customValidatorsMap.get(key);
        if (customValidatorFn) {
          control.removeValidators(customValidatorFn);
          this.customValidatorsMap.delete(key);
        }
      }

      control.updateValueAndValidity();
    });
  }

  getInitialRegistrationFormInfo(a2PRegistrationInfo: A2PRegistrationInfoInterface): Map<string, string> {
    const result = new Map<string, string>();
    result.set('businessName', a2PRegistrationInfo?.business?.legalName || '');
    result.set('businessCategory', a2PRegistrationInfo?.business?.industry || '');
    result.set('businessType', a2PRegistrationInfo?.business?.businessType || '');
    result.set('regionOfOperation', a2PRegistrationInfo?.business?.regionOfOperation || '');
    result.set('businessRegistrationNumber', a2PRegistrationInfo?.business?.registrationIdentifier?.identifier || '');
    result.set('address', a2PRegistrationInfo?.business?.streetAddress || '');
    result.set('additionalAddressLine', a2PRegistrationInfo?.business?.streetAddressSecondary || '');
    result.set('state', a2PRegistrationInfo?.business?.state || '');
    result.set('city', a2PRegistrationInfo?.business?.city || '');
    result.set('zipCode', a2PRegistrationInfo?.business?.postalCode || '');
    result.set('website', a2PRegistrationInfo?.business?.websiteUrl || '');
    result.set('firstName', a2PRegistrationInfo?.authorizedRepresentative?.firstName || '');
    result.set('lastName', a2PRegistrationInfo?.authorizedRepresentative?.lastName || '');
    result.set('jobPosition', a2PRegistrationInfo?.authorizedRepresentative?.jobPosition || '');
    result.set('businessTitle', a2PRegistrationInfo?.authorizedRepresentative?.businessTitle || '');
    result.set('email', a2PRegistrationInfo?.authorizedRepresentative?.email || '');
    result.set('phoneNumber', a2PRegistrationInfo?.authorizedRepresentative?.phoneNumber || '');
    return result;
  }

  sameValueValidator(originalFormFieldValue: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (originalFormFieldValue === control.value) {
        return { sameValueError: true };
      }
      return null;
    };
  }

  someValueNeedsToChangeValidator(groupOfFields: string[]) {
    const originalValuesMap: Map<string, string> = new Map<string, string>();
    groupOfFields.forEach((fieldName) => {
      const val = this.initialRegistrationFormInfo.get(fieldName);
      if (val === undefined) {
        return;
      }
      originalValuesMap.set(fieldName, val);
    });

    return (): ValidationErrors | null => {
      let passed = false;
      for (const [fieldName, originalValue] of originalValuesMap.entries()) {
        const c = this.smsForm.get(fieldName);
        if (c !== null && c.value !== originalValue) {
          passed = true;
          break;
        }
      }
      if (!passed) {
        return { groupValueError: true };
      }
      return null;
    };
  }

  getFormFieldsWithIssues(registrationStatus: A2PRegistrationStatusInterface): string[] {
    const result: string[] = [];
    if (registrationStatus === null || registrationStatus === undefined) {
      return [];
    }
    if (
      !!registrationStatus.secondaryBusinessProfile &&
      registrationStatus.secondaryBusinessProfile.status === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT &&
      registrationStatus.secondaryBusinessProfile.fieldsWithIssues &&
      registrationStatus.secondaryBusinessProfile.fieldsWithIssues.length > 0
    ) {
      registrationStatus.secondaryBusinessProfile.fieldsWithIssues.forEach((field) => {
        const val = this.fieldWithIssuesToFormFieldsMapping.get(field);
        if (val !== undefined) {
          result.push(val);
        }
      });
    }
    if (
      !!registrationStatus.trustBundle &&
      registrationStatus.trustBundle.status === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT &&
      !!registrationStatus.trustBundle.fieldsWithIssues &&
      registrationStatus.trustBundle.fieldsWithIssues.length > 0
    ) {
      registrationStatus.trustBundle.fieldsWithIssues.forEach((field) => {
        const val = this.fieldWithIssuesToFormFieldsMapping.get(field);
        if (val !== undefined) {
          result.push(val);
        }
      });
    }

    return result;
  }

  getGroupOfSomeValuesNeedToChange(registrationStatus: A2PRegistrationStatusInterface): string[] {
    const result: string[] = [];
    if (registrationStatus === null || registrationStatus === undefined) {
      return [];
    }

    if (!!registrationStatus.brand && registrationStatus.brand.status === BrandStatus.BRAND_STATUS_FAILED) {
      result.push(businessRegistrationNumber);
      result.push(businessName);
    }

    return result;
  }

  prefillFormWithA2PFormInfo(a2pRegistrationInfo: A2PRegistrationInfoInterface): void {
    this.smsForm.patchValue({
      businessName: a2pRegistrationInfo?.business?.legalName,
      businessCategory: a2pRegistrationInfo?.business?.industry,
      businessType: a2pRegistrationInfo?.business?.businessType,
      regionOfOperation: a2pRegistrationInfo?.business?.regionOfOperation,
      businessRegistrationNumber: a2pRegistrationInfo?.business?.registrationIdentifier?.identifier,
      address: a2pRegistrationInfo?.business?.streetAddress,
      additionalAddressLine: a2pRegistrationInfo?.business?.streetAddressSecondary,
      state: a2pRegistrationInfo?.business?.state,
      city: a2pRegistrationInfo?.business?.city,
      zipCode: a2pRegistrationInfo?.business?.postalCode,
      website: a2pRegistrationInfo?.business?.websiteUrl,
      firstName: a2pRegistrationInfo?.authorizedRepresentative?.firstName,
      lastName: a2pRegistrationInfo?.authorizedRepresentative?.lastName,
      jobPosition: a2pRegistrationInfo?.authorizedRepresentative?.jobPosition,
      businessTitle: a2pRegistrationInfo?.authorizedRepresentative?.businessTitle,
      email: a2pRegistrationInfo?.authorizedRepresentative?.email,
      phoneNumber: a2pRegistrationInfo?.authorizedRepresentative?.phoneNumber,
    });
  }
}
